import React from 'react'
import { useOutletContext } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Drawer,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/AddRounded'
import DeleteIcon from '@mui/icons-material/DeleteOutlineRounded'
import EditIcon from '@mui/icons-material/EditRounded'
import PlayArrowIcon from '@mui/icons-material/PlayArrowRounded'
import DragIndicatorIcon from '@mui/icons-material/DragIndicatorRounded'
import CloseIcon from '@mui/icons-material/CloseRounded'
import { DndContext, KeyboardSensor, PointerSensor, type DragEndEvent, useSensor, useSensors } from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { MeResponse } from '../../api/me'
import { usePageTitle } from '../../components/common/PageTitleContext'
import useAutoResponderStore, {
  type AutoResponderQueryEnabledFilter,
  type AutoResponderRuleDraft,
  type AutoResponderRuleTriggerDraft,
} from '../../store/autoResponder'
import type {
  AutoResponderRule,
  AutoResponderConflictWarning,
  AutoResponderTestMatch,
} from '../../api/autoResponder'

interface OutletContextValue {
  me: MeResponse
  includeSuperadmin: boolean
}

const ENABLED_FILTERS: AutoResponderQueryEnabledFilter[] = ['all', 'enabled', 'disabled']
const RULE_EDITOR_DRAWER_WIDTH = 506

const formatActiveDate = (iso: string | null): string => {
  if (!iso) {
    return 'Always'
  }
  const offsetMatch = iso.match(/([+-]\d{2}:\d{2}|Z)$/)
  const offsetLabel = offsetMatch ? (offsetMatch[1] === 'Z' ? 'UTC' : `UTC${offsetMatch[1]}`) : ''
  const baseMatch = iso.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/)
  if (baseMatch) {
    const formatted = `${baseMatch[1]} ${baseMatch[2]}`
    return offsetLabel ? `${formatted} (${offsetLabel})` : formatted
  }
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return iso
  }
  return offsetLabel
    ? `${new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(date)} (${offsetLabel})`
    : new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(date)
}

const formatActiveWindow = (rule: AutoResponderRule): string => {
  if (!rule.active_from && !rule.active_until) {
    return 'Always active'
  }
  if (rule.active_from && rule.active_until) {
    return `${formatActiveDate(rule.active_from)} – ${formatActiveDate(rule.active_until)}`
  }
  if (rule.active_from) {
    return `From ${formatActiveDate(rule.active_from)}`
  }
  return `Until ${formatActiveDate(rule.active_until)}`
}

const formatLastFired = (iso: string | null): string => {
  if (!iso) {
    return 'Never'
  }
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return 'Unknown'
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

const TriggerChips: React.FC<{ triggers: AutoResponderRule['triggers'] }> = ({ triggers }) => (
  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
    {triggers.map((trigger) => (
      <Chip
        key={`${trigger.id}-${trigger.match_operator}-${trigger.trigger_text}`}
        size="small"
        label={`${trigger.match_operator.replace('_', ' ')}: ${trigger.trigger_text}`}
      />
    ))}
  </Stack>
)

interface SortableRuleRowProps {
  rule: AutoResponderRule
  onEdit: (ruleId: number) => void
  onDelete: (ruleId: number) => void
  onToggle: (ruleId: number, enabled: boolean) => void
}

const SortableRuleRow: React.FC<SortableRuleRowProps> = ({ rule, onEdit, onDelete, onToggle }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: rule.id,
  })

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.72 : 1,
    backgroundColor: isDragging ? 'rgba(0,0,0,0.04)' : undefined,
    borderRadius: 16,
  }

  const hasWarnings = Array.isArray(rule.conflict_warnings) && rule.conflict_warnings.length > 0

  return (
    <Paper ref={setNodeRef} style={style} sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <IconButton
          size="small"
          color="inherit"
          {...attributes}
          {...listeners}
          ref={setActivatorNodeRef}
          aria-label="Drag to reorder"
        >
          <DragIndicatorIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {rule.name}
            {hasWarnings ? (
              <Tooltip title="This rule has potential conflicts with other rules">
                <Chip color="warning" size="small" label="Warnings" />
              </Tooltip>
            ) : null}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatActiveWindow(rule)}
          </Typography>
        </Box>
        <FormControlLabel
          control={<Switch checked={rule.enabled} onChange={(event) => onToggle(rule.id, event.target.checked)} />}
          label={rule.enabled ? 'Enabled' : 'Disabled'}
        />
        <Tooltip title="Edit rule">
          <IconButton size="small" color="primary" onClick={() => onEdit(rule.id)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete rule">
          <IconButton size="small" color="error" onClick={() => onDelete(rule.id)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      <Divider flexItem sx={{ my: 1 }} />
      <Stack spacing={1}>
        <Typography variant="body2" color="text.secondary">
          <strong>Response preview:</strong> {rule.response_text || '—'}
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
            Triggers:
          </Typography>
          <TriggerChips triggers={rule.triggers} />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Last fired: {formatLastFired(rule.last_fired_at)}
        </Typography>
      </Stack>
    </Paper>
  )
}

interface RuleEditorDialogProps {
  open: boolean
  draft: AutoResponderRuleDraft | null
  saving: boolean
  errors: Record<string, string | undefined>
  warnings: AutoResponderConflictWarning[]
  errorMessage: string | null
  onClose: () => void
  onSave: () => Promise<void>
  onChangeField: <K extends keyof AutoResponderRuleDraft>(key: K, value: AutoResponderRuleDraft[K]) => void
  onAddTrigger: () => void
  onUpdateTrigger: (index: number, updates: Partial<AutoResponderRuleTriggerDraft>) => void
  onRemoveTrigger: (index: number) => void
}

const RuleEditorDialog: React.FC<RuleEditorDialogProps> = ({
  open,
  draft,
  saving,
  errors,
  warnings,
  errorMessage,
  onClose,
  onSave,
  onChangeField,
  onAddTrigger,
  onUpdateTrigger,
  onRemoveTrigger,
}) => {
  const handleSubmit = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      await onSave()
    },
    [onSave],
  )

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
        sx: {
          width: { xs: '100%', md: RULE_EDITOR_DRAWER_WIDTH },
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6">{draft?.id ? 'Edit rule' : 'New rule'}</Typography>
        <IconButton onClick={onClose} aria-label="Close editor">
          <CloseIcon />
        </IconButton>
      </Stack>
      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
        {errorMessage ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        ) : null}
        {warnings.length > 0 ? (
          <Stack spacing={1} sx={{ mb: 2 }}>
            {warnings.map((warning) => (
              <Alert key={`${warning.type}-${warning.other_rule_id}-${warning.message}`} severity="warning">
                {warning.message}
              </Alert>
            ))}
          </Stack>
        ) : null}
        <Alert severity="info" sx={{ mb: 2 }}>
          Rules run from top to bottom and only the first enabled match sends a reply. Keep triggers mutually
          exclusive to avoid conflict warnings.
        </Alert>
        <Stack spacing={2}>
          <TextField
            label="Rule name"
            value={draft?.name ?? ''}
            onChange={(event) => onChangeField('name', event.target.value)}
            required
            fullWidth
            error={Boolean(errors.name)}
            helperText={errors.name}
          />
          <FormControlLabel
            control={
              <Switch
                checked={draft?.enabled ?? true}
                onChange={(event) => onChangeField('enabled', event.target.checked)}
              />
            }
            label={draft?.enabled ? 'Enabled' : 'Disabled'}
          />
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2">Triggers</Typography>
              <Button variant="outlined" size="small" onClick={onAddTrigger}>
                Add trigger
              </Button>
            </Stack>
            {draft?.triggers.map((trigger, index) => (
              <Paper key={`trigger-${index}`} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      fullWidth
                      label="Operator"
                      value={trigger.match_operator}
                      onChange={(event) =>
                        onUpdateTrigger(index, {
                          match_operator: event.target.value as AutoResponderRuleTriggerDraft['match_operator'],
                        })
                      }
                    >
                      <MenuItem value="contains">Contains</MenuItem>
                      <MenuItem value="starts_with">Starts with</MenuItem>
                      <MenuItem value="equals">Equals</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <TextField
                      label="Trigger phrase"
                      value={trigger.trigger_text}
                      onChange={(event) => onUpdateTrigger(index, { trigger_text: event.target.value })}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={1} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                    <Tooltip title="Remove trigger">
                      <span>
                        <IconButton
                          color="error"
                          size="small"
                          disabled={draft.triggers.length <= 1}
                          onClick={() => onRemoveTrigger(index)}
                          aria-label="Remove trigger"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Paper>
            ))}
            {errors.triggers ? (
              <Typography variant="body2" color="error">
                {errors.triggers}
              </Typography>
            ) : null}
          </Stack>
          <TextField
            label="Response text"
            value={draft?.response_text ?? ''}
            onChange={(event) => onChangeField('response_text', event.target.value)}
            fullWidth
            multiline
            minRows={4}
            helperText={
              errors.response_text ? errors.response_text : `${draft?.response_text?.length ?? 0} characters`
            }
            error={Boolean(errors.response_text)}
          />
          <Stack spacing={2}>
            <Typography variant="subtitle2">Active window</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Active from"
                  type="datetime-local"
                  value={draft?.active_from ?? ''}
                  onChange={(event) => onChangeField('active_from', event.target.value || null)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(errors.active_from)}
                  helperText={errors.active_from ?? 'Leave blank to start immediately.'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Active until"
                  type="datetime-local"
                  value={draft?.active_until ?? ''}
                  onChange={(event) => onChangeField('active_until', event.target.value || null)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(errors.active_until)}
                  helperText={errors.active_until ?? 'Leave blank for no end date.'}
                />
              </Grid>
            </Grid>
            <Typography variant="caption" color="text.secondary">
              Times are saved in the station's WordPress timezone.
            </Typography>
          </Stack>
        </Stack>
      </Box>
      <Divider />
      <Stack direction="row" spacing={2} sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button variant="outlined" onClick={onClose} fullWidth>
          Cancel
        </Button>
        <Button type="submit" variant="contained" disabled={saving} fullWidth>
          {saving ? 'Saving…' : 'Save rule'}
        </Button>
      </Stack>
    </Drawer>
  )
}

const SimulatorPanel: React.FC<{
  messageText: string
  normalisedText: string
  match: AutoResponderTestMatch | null
  warnings: AutoResponderConflictWarning[]
  loading: boolean
  error: string | null
  onChange: (value: string) => void
  onRun: (useDraft: boolean) => void
}> = ({ messageText, normalisedText, match, warnings, loading, error, onChange, onRun }) => {
  const [useDraft, setUseDraft] = React.useState(false)

  return (
    <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
      <Stack spacing={2}>
        <Typography variant="h6">Simulator</Typography>
        <TextField
          label="Sample incoming message"
          value={messageText}
          onChange={(event) => onChange(event.target.value)}
          multiline
          minRows={4}
          placeholder="Type a listener message to test the auto responder…"
          helperText="Run the simulator to see how the text is normalised and which rule would respond."
        />
        <FormControlLabel
          control={<Switch checked={useDraft} onChange={(event) => setUseDraft(event.target.checked)} />}
          label="Include current draft rule"
        />
        <Button
          variant="contained"
          startIcon={<PlayArrowIcon />}
          onClick={() => onRun(useDraft)}
          disabled={loading || messageText.trim().length === 0}
        >
          {loading ? 'Running…' : 'Run simulator'}
        </Button>
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : null}
        {warnings.length > 0 ? (
          <Stack spacing={1}>
            {warnings.map((warning) => (
              <Alert key={`${warning.type}-${warning.other_rule_id}`} severity="warning">
                {warning.message}
              </Alert>
            ))}
          </Stack>
        ) : null}
        {normalisedText ? (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Normalised text
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
              {normalisedText}
            </Paper>
          </Box>
        ) : null}
        {match ? (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Predicted match
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>Rule:</strong> {match.rule_name} (#{match.rule_id})
              </Typography>
              <Typography variant="body2">
                <strong>Trigger:</strong> {match.trigger_operator.replace('_', ' ')} “{match.trigger_text}”
              </Typography>
              <Typography variant="body2">
                <strong>Source:</strong> {match.source === 'draft' ? 'Current draft' : 'Existing rule'}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" color="text.secondary">
                <strong>Response preview:</strong>
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {match.response_text}
              </Typography>
            </Paper>
          </Box>
        ) : normalisedText && !loading ? (
          <Alert severity="info">No matching rule found.</Alert>
        ) : null}
      </Stack>
    </Paper>
  )
}

const AutoResponderPage: React.FC = () => {
  const { me } = useOutletContext<OutletContextValue>()
  const stationId = me.stations?.[0]?.id ?? null

  usePageTitle('Auto Responder', { align: 'center' })

  const rules = useAutoResponderStore((state) => state.rules)
  const pagination = useAutoResponderStore((state) => state.pagination)
  const loading = useAutoResponderStore((state) => state.loading)
  const error = useAutoResponderStore((state) => state.error)
  const query = useAutoResponderStore((state) => state.query)
  const fetchRules = useAutoResponderStore((state) => state.fetchRules)
  const resetStore = useAutoResponderStore((state) => state.reset)
  const openCreateEditor = useAutoResponderStore((state) => state.openCreateEditor)
  const openEditEditor = useAutoResponderStore((state) => state.openEditEditor)
  const closeEditor = useAutoResponderStore((state) => state.closeEditor)
  const editor = useAutoResponderStore((state) => state.editor)
  const saveEditor = useAutoResponderStore((state) => state.saveEditor)
  const setDraftField = useAutoResponderStore((state) => state.setDraftField)
  const addDraftTrigger = useAutoResponderStore((state) => state.addDraftTrigger)
  const updateDraftTrigger = useAutoResponderStore((state) => state.updateDraftTrigger)
  const removeDraftTrigger = useAutoResponderStore((state) => state.removeDraftTrigger)
  const deleteRule = useAutoResponderStore((state) => state.deleteRule)
  const toggleRuleEnabled = useAutoResponderStore((state) => state.toggleRuleEnabled)
  const reorderRules = useAutoResponderStore((state) => state.reorderRules)
  const lastMutationWarnings = useAutoResponderStore((state) => state.lastMutationWarnings)
  const lastMutationMessage = useAutoResponderStore((state) => state.lastMutationMessage)
  const lastMutationSeverity = useAutoResponderStore((state) => state.lastMutationSeverity)
  const resetMutationState = useAutoResponderStore((state) => state.resetMutationState)
  const simulator = useAutoResponderStore((state) => state.simulator)
  const setSimulatorMessage = useAutoResponderStore((state) => state.setSimulatorMessage)
  const runSimulator = useAutoResponderStore((state) => state.runSimulator)

  const [searchValue, setSearchValue] = React.useState('')
  const searchTimeoutRef = React.useRef<number | null>(null)

  React.useEffect(() => {
    setSearchValue(query.search)
  }, [query.search])

  React.useEffect(() => {
    if (!stationId) {
      resetStore()
      return
    }
    resetStore()
    fetchRules(stationId, { page: 1 })
  }, [stationId, fetchRules, resetStore])

  React.useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        window.clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setSearchValue(value)
    if (!stationId) {
      return
    }
    if (searchTimeoutRef.current) {
      window.clearTimeout(searchTimeoutRef.current)
    }
    searchTimeoutRef.current = window.setTimeout(() => {
      fetchRules(stationId, { search: value, page: 1 })
    }, 350)
  }

  const handleEnabledFilterChange = (
    _event: React.MouseEvent<HTMLElement>,
    next: AutoResponderQueryEnabledFilter | null,
  ) => {
    if (!stationId) {
      return
    }
    const nextValue = next ?? 'all'
    if (nextValue === query.enabled) {
      return
    }
    fetchRules(stationId, { enabled: nextValue, page: 1 })
  }

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    if (!stationId || page === query.page) {
      return
    }
    fetchRules(stationId, { page })
  }

  const handleEditRule = (ruleId: number) => {
    openEditEditor(ruleId)
  }

  const handleDeleteRule = async (ruleId: number) => {
    const rule = rules.find((item) => item.id === ruleId)
    const name = rule?.name ?? 'this rule'
    if (!window.confirm(`Delete ${name}? This cannot be undone.`)) {
      return
    }
    await deleteRule(ruleId)
  }

  const handleToggleEnabled = async (ruleId: number, enabled: boolean) => {
    await toggleRuleEnabled(ruleId, enabled)
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) {
      return
    }
    const currentOrder = rules.map((rule) => rule.id)
    const oldIndex = currentOrder.indexOf(Number(active.id))
    const newIndex = currentOrder.indexOf(Number(over.id))
    if (oldIndex === -1 || newIndex === -1) {
      return
    }
    const nextOrder = arrayMove(currentOrder, oldIndex, newIndex)
    await reorderRules(nextOrder)
  }

  const handleSaveEditor = React.useCallback(async () => {
    await saveEditor()
  }, [saveEditor])

  const handleRunSimulator = (useDraft: boolean) => {
    runSimulator({ useDraft })
  }

  if (!stationId) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="info">Select a station to manage auto responder rules.</Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ position: 'relative', minHeight: '100%', pb: editor.open ? 0 : 6 }}>
      {loading ? <LinearProgress sx={{ position: 'sticky', top: 0, left: 0, right: 0, zIndex: 5 }} /> : null}
      <Stack spacing={3} sx={{ px: { xs: 2, md: 3 }, py: 3, maxWidth: 1200, mx: 'auto' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          <Stack spacing={1} sx={{ flex: 1 }}>
            <Typography variant="h4" fontWeight={700}>
              Auto Responder
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Create ordered rules that automatically reply to listeners when their messages match specific triggers.
            </Typography>
          </Stack>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateEditor} sx={{ alignSelf: { xs: 'stretch', md: 'flex-end' } }}>
            New rule
          </Button>
        </Stack>

        {lastMutationMessage ? (
          <Alert
            severity={lastMutationSeverity ?? 'info'}
            onClose={resetMutationState}
          >
            {lastMutationMessage}
          </Alert>
        ) : null}
        {lastMutationWarnings.length > 0 ? (
          <Stack spacing={1}>
            {lastMutationWarnings.map((warning) => (
              <Alert key={`${warning.type}-${warning.other_rule_id}-${warning.message}`} severity="warning">
                {warning.message}
              </Alert>
            ))}
          </Stack>
        ) : null}
        {error ? <Alert severity="error">{error}</Alert> : null}

        <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
              <TextField
                fullWidth
                label="Search rules"
                value={searchValue}
                onChange={handleSearchChange}
                placeholder="Search by name or response text"
              />
              <ToggleButtonGroup
                value={query.enabled}
                exclusive
                onChange={handleEnabledFilterChange}
                aria-label="Filter by status"
                sx={{ flexShrink: 0 }}
              >
                {ENABLED_FILTERS.map((filter) => (
                  <ToggleButton key={filter} value={filter} aria-label={filter}>
                    {filter === 'all' ? 'All' : filter === 'enabled' ? 'Enabled' : 'Disabled'}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Stack>

            {rules.length === 0 && !loading ? (
              <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>
                  No auto responder rules yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Create your first rule to automatically reply to common listener messages.
                </Typography>
                <Button variant="contained" onClick={openCreateEditor} startIcon={<AddIcon />}>
                  Create rule
                </Button>
              </Paper>
            ) : null}

            {rules.length > 0 ? (
              <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                <SortableContext items={rules.map((rule) => rule.id)} strategy={verticalListSortingStrategy}>
                  <Stack spacing={2}>
                    {rules.map((rule) => (
                      <SortableRuleRow
                        key={rule.id}
                        rule={rule}
                        onEdit={handleEditRule}
                        onDelete={handleDeleteRule}
                        onToggle={handleToggleEnabled}
                      />
                    ))}
                  </Stack>
                </SortableContext>
              </DndContext>
            ) : null}

            {pagination.total_pages > 1 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  count={pagination.total_pages}
                  page={query.page}
                  onChange={handlePageChange}
                  color="primary"
                  shape="rounded"
                />
              </Box>
            ) : null}
          </Stack>
        </Paper>

        <SimulatorPanel
          messageText={simulator.messageText}
          normalisedText={simulator.normalisedText}
          match={simulator.match}
          warnings={simulator.warnings}
          loading={simulator.loading}
          error={simulator.error}
          onChange={setSimulatorMessage}
          onRun={handleRunSimulator}
        />
      </Stack>

      <RuleEditorDialog
        open={editor.open}
        draft={editor.draft}
        saving={editor.saving}
        errors={editor.errors}
        warnings={editor.warnings}
        errorMessage={editor.errorMessage}
        onClose={closeEditor}
        onSave={handleSaveEditor}
        onChangeField={setDraftField}
        onAddTrigger={() => addDraftTrigger()}
        onUpdateTrigger={updateDraftTrigger}
        onRemoveTrigger={removeDraftTrigger}
      />
    </Box>
  )
}

export default AutoResponderPage
